import React from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Divider,
  TableContainer,
  Paper,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, red } from "@mui/material/colors";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { formatDate } from "../../../utils/utils";

const UserViewDialog = ({
  openModal,
  setOpenModal,
  setSelectedUserEmail,
  selectedUserDetail = {},
  setSelectedUserDetail = () => {},
} = {}) => {
  const handleCloseModal = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return; // Prevent closing when clicked outside
    }
    setSelectedUserEmail(null);
    setSelectedUserDetail({});
    setOpenModal(false);
  };

  const {
    firstName,
    lastName,
    email,
    lanId,
    requestType,
    lastLoginDate,
    createdTime,
    updatedTime,
    createdBy,
    updatedBy,
    regions,
    admin,
    applicationAdministrator,
    globalTechSupport,
    userAccessProvisioner,
    active,
  } = selectedUserDetail || {};
  return (
    <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle>
        Details of {firstName} {lastName}{" "}
        {admin && (
          <StarIcon
            sx={{
              fontSize: "22px",
              marginRight: "15px",
              marginBottom: "-4px",
              color: "#ffd600",
            }}
          />
        )}
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ mt: 0.5 }} />
      <DialogContent>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <InfoGridRow label="First Name" value={firstName} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Last Name" value={lastName} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Email" value={email} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Lan Id" value={lanId} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Request Type" value={requestType} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Active" value={active} isIcon />
          </Grid>
          <Grid item>
            <InfoGridRow label="Admin" value={admin} isIcon />
          </Grid>
          <Grid item>
            <InfoGridRow
              label="Application Administrator"
              value={applicationAdministrator}
              isIcon
            />
          </Grid>
          <Grid item>
            <InfoGridRow
              label="Global Tech Support"
              value={globalTechSupport}
              isIcon
            />
          </Grid>
          <Grid item>
            <InfoGridRow
              label="User Access Provisioner"
              value={userAccessProvisioner}
              isIcon
            />
          </Grid>
          <Grid item>
            <InfoGridRow label="Last Login" value={formatDate(lastLoginDate)} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Created Time" value={formatDate(createdTime)} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Updated Time" value={formatDate(updatedTime)} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Created By" value={createdBy} />
          </Grid>
          <Grid item>
            <InfoGridRow label="Updated By" value={updatedBy} />
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          Region & Business Entity IDs
        </Typography>
        <Divider sx={{ mt: 0.5 }} />
        <Table size="small" aria-label="region-sfc-table">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Region Name</strong>
              </TableCell>
              <TableCell>
                <strong>Business Entity ID</strong>
              </TableCell>
              <TableCell>
                <strong>Local Currency</strong>
              </TableCell>
              <TableCell>
                <strong>Country</strong>
              </TableCell>
              <TableCell>
                <strong>Readable</strong>
              </TableCell>
              <TableCell>
                <strong>Writable</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {regions?.map((region) =>
              region?.sfcs?.map((sfc, idx) => (
                <TableRow key={`${region?.regionId}-${sfc?.sfcId}`}>
                  <TableCell>{region?.regionName}</TableCell>
                  <TableCell>{sfc?.businessEntityID}</TableCell>
                  <TableCell>{sfc?.localCurrency}</TableCell>
                  <TableCell>{sfc?.country}</TableCell>
                  <TableCell>
                    <CheckIcon sx={{ color: green[500] }} />
                  </TableCell>
                  <TableCell>
                    {sfc?.writable ? (
                      <CheckIcon sx={{ color: green[500] }} />
                    ) : (
                      <CloseIcon sx={{ color: red[400] }} />
                    )}
                  </TableCell>
                </TableRow>
              )),
            )}
          </TableBody>
        </Table>

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          Granted Page Roles
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            maxWidth: 300,
            width: "100%",
          }}
        >
          <Table size="small" aria-label="page-role-table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Page Role Names</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regions?.map((region) =>
                region?.sfcs?.map((sfc, idx) => (
                  <>
                    {sfc?.writable ? (
                      <>
                        <TableRow
                          key={`${region?.regionId}-${sfc?.sfcId}-write`}
                        >
                          <TableCell>
                            {sfc?.businessEntityID} - Write (SFC)
                          </TableCell>
                        </TableRow>
                        <TableRow
                          key={`${region?.regionId}-${sfc?.sfcId}-read`}
                        >
                          <TableCell>
                            {sfc?.businessEntityID} - Read (SFC)
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow key={`${region?.regionId}-${sfc?.sfcId}-read`}>
                        <TableCell>
                          {sfc?.businessEntityID} - Read (SFC)
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )),
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <DialogActions sx={{ mt: 2 }}>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

const InfoGridRow = ({ label, value, isIcon = false }) => (
  <Grid container alignItems="center" spacing={1} wrap="nowrap">
    <Grid item sx={{ width: 150, pl: 2 }}>
      <Typography variant="body1" fontWeight="bold">
        {label}
      </Typography>
    </Grid>
    <Grid item sx={{ width: 10 }}>
      <Typography variant="body1">:</Typography>
    </Grid>
    <Grid item xs>
      {isIcon ? (
        value ? (
          <CheckCircleIcon color="success" />
        ) : (
          <CancelIcon color="error" />
        )
      ) : (
        <Typography variant="body1">{value || "N/A"}</Typography>
      )}
    </Grid>
  </Grid>
);

export default UserViewDialog;
